import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, CheckCircleIcon, FileDownIcon, FileTextIcon, ChevronDownIcon, ChevronUpIcon, SendIcon, RefreshCwIcon, XIcon, AlertCircleIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { StatusBadge, ExamStatus } from '../components/ui/StatusBadge';
import { useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
// --- Mock Data ---
const mockExams = [{
  id: 'EXM-5001',
  patient: 'Marco Rossi',
  email: 'marco.rossi@email.it',
  telefono: '+39 333 1234567',
  codiceFiscale: 'RSSMRC80A01H501Z',
  type: 'Risonanza magnetica encefalo',
  category: 'RM',
  doctor: 'Dr. S. Verdi',
  machine: 'Siemens Magnetom',
  date: '2026-03-11',
  time: '08:00',
  status: 'In corso' as ExamStatus
}, {
  id: 'EXM-5002',
  patient: 'Giulia Bianchi',
  email: 'giulia.bianchi@email.it',
  telefono: '+39 334 2345678',
  codiceFiscale: 'BNCGLI85B42F205X',
  type: 'TAC torace',
  category: 'TAC',
  doctor: 'Dr. A. Neri',
  machine: 'GE Revolution',
  date: '2026-03-11',
  time: '09:30',
  status: 'In attesa' as ExamStatus
}, {
  id: 'EXM-5004',
  patient: 'Elena Colombo',
  email: 'elena.colombo@email.it',
  telefono: '+39 336 4567890',
  codiceFiscale: 'CLMLNE90D50F205W',
  type: 'Ecografia addome',
  category: 'ECO',
  doctor: 'Dr. S. Verdi',
  machine: 'Philips Affiniti',
  date: '2026-03-11',
  time: '11:00',
  status: 'Da refertare' as ExamStatus
}, {
  id: 'EXM-5006',
  patient: 'Anna Ferrari',
  email: 'anna.ferrari@email.it',
  telefono: '+39 338 6789012',
  codiceFiscale: 'FRRNNA88F45F205U',
  type: 'TAC addome',
  category: 'TAC',
  doctor: 'Dr. A. Neri',
  machine: 'GE Revolution',
  date: '2026-03-10',
  time: '15:45',
  status: 'Refertato' as ExamStatus
}, {
  id: 'EXM-5007',
  patient: 'Giuseppe Russo',
  email: 'giuseppe.russo@email.it',
  telefono: '+39 339 7890123',
  codiceFiscale: 'RSSGPP70G18H501T',
  type: 'Ecografia pelvi',
  category: 'ECO',
  doctor: 'Dr. M. Gialli',
  machine: 'Philips Affiniti',
  date: '2026-03-10',
  time: '16:30',
  status: 'Consegnato' as ExamStatus
}, {
  id: 'EXM-4982',
  patient: 'Marco Rossi',
  email: 'marco.rossi@email.it',
  telefono: '+39 333 1234567',
  codiceFiscale: 'RSSMRC80A01H501Z',
  type: 'TAC torace',
  category: 'TAC',
  doctor: 'Dr. A. Neri',
  machine: 'GE Revolution',
  date: '2025-11-20',
  time: '10:30',
  status: 'Consegnato' as ExamStatus
}, {
  id: 'EXM-4521',
  patient: 'Marco Rossi',
  email: 'marco.rossi@email.it',
  telefono: '+39 333 1234567',
  codiceFiscale: 'RSSMRC80A01H501Z',
  type: 'Radiografia colonna vertebrale',
  category: 'RX',
  doctor: 'Dr. M. Gialli',
  machine: 'Carestream DRX',
  date: '2025-06-15',
  time: '09:15',
  status: 'Refertato' as ExamStatus
}];
const mockTemplates = [{
  id: 't1',
  category: 'RM',
  name: 'Referto RM Standard',
  description: 'Modello base per risonanza magnetica con sezioni standard.'
}, {
  id: 't2',
  category: 'RM',
  name: 'Referto RM con Contrasto',
  description: 'Modello per risonanza magnetica con mezzo di contrasto.'
}, {
  id: 't3',
  category: 'TAC',
  name: 'Referto TAC Standard',
  description: 'Modello base per tomografia assiale computerizzata.'
}, {
  id: 't4',
  category: 'ECO',
  name: 'Referto Ecografia Addome',
  description: 'Modello specifico per ecografia addominale completa.'
}, {
  id: 't5',
  category: 'RX',
  name: 'Referto RX Standard',
  description: 'Modello base per esami radiografici tradizionali.'
}, {
  id: 't6',
  category: 'MAMMO',
  name: 'Referto Mammografia',
  description: 'Modello per screening mammografico bilaterale.'
}];
function formatTimestamp(date: Date): string {
  const day = date.getDate();
  const months = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}
export function ReportEditorPage() {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const {
    role
  } = useRole();
  const CURRENT_MEDICO = 'Dr. S. Verdi';
  const isMedico = role === 'medico';
  const initialExam = mockExams.find(e => e.id === id) || mockExams[2];
  // Enforce ownership: when medico, the reporting doctor is always the current user
  const [exam, setExam] = useState(() => {
    if (isMedico) {
      return {
        ...initialExam,
        doctor: CURRENT_MEDICO
      };
    }
    return initialExam;
  });
  const availableTemplates = mockTemplates.filter(t => t.category === exam.category);
  const [selectedTemplate, setSelectedTemplate] = useState(availableTemplates.length > 0 ? availableTemplates[0].id : '');
  const [patientInfoOpen, setPatientInfoOpen] = useState(false);
  const [examInfoOpen, setExamInfoOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const hasExistingContent = exam.status === 'Refertato' || exam.status === 'Consegnato';
  const [reportForm, setReportForm] = useState({
    intro: hasExistingContent ? 'Paziente presenta cefalea ricorrente da circa 3 settimane.' : '',
    rilevazioni: hasExistingContent ? 'Ventricoli e solchi di dimensioni e configurazione normali. Nessuna evidenza di effetto massa, spostamento della linea mediana o emorragia intracranica acuta.' : '',
    clinica: hasExistingContent ? 'Esame nei limiti della norma. Nessuna anomalia intracranica acuta.' : '',
    conclusioni: hasExistingContent ? 'Studio normale.' : ''
  });
  // Autosave UX Fix
  const [saveStatus, setSaveStatus] = useState<'idle' | 'unsaved' | 'saving' | 'saved' | 'error'>('idle');
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isFirstRender = useRef(true);
  const performAutosave = useCallback(() => {
    setSaveStatus('saving');
    setTimeout(() => {
      setLastSaved(new Date());
      setSaveStatus('saved');
    }, 800);
  }, []);
  // Configurable autosave interval
  const autosaveIntervalSec = parseInt(localStorage.getItem('autosaveInterval') || '45', 10);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setSaveStatus('unsaved');
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }
    autosaveTimerRef.current = setTimeout(() => {
      performAutosave();
    }, autosaveIntervalSec * 1000);
    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [reportForm, performAutosave, autosaveIntervalSec]);
  // Toast Feedback
  const [toastMessage, setToastMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);
  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({
      type,
      text
    });
    setTimeout(() => setToastMessage(null), 4000);
  };
  const handleConfirmReport = (sendToPatient: boolean) => {
    setExam(prev => ({
      ...prev,
      status: sendToPatient ? 'Consegnato' : 'Refertato'
    }));
    if (sendToPatient) {
      showToast('success', 'Referto inviato correttamente al paziente.');
    } else {
      showToast('success', 'Referto salvato e confermato.');
    }
  };
  const handleSendToPatient = () => {
    setExam(prev => ({
      ...prev,
      status: 'Consegnato'
    }));
    showToast('success', 'Referto inviato correttamente al paziente.');
  };
  const handleDownloadPDF = () => {
    showToast('success', 'Download PDF avviato');
    setTimeout(() => {
      const content = `REFERTO MEDICO\n\nPaziente: ${exam.patient}\nEsame: ${exam.type}\nData: ${exam.date}\n\n--- INTRODUZIONE ---\n${reportForm.intro}\n\n--- REPERTI ---\n${reportForm.rilevazioni}\n\n--- VALUTAZIONE CLINICA ---\n${reportForm.clinica}\n\n--- CONCLUSIONI ---\n${reportForm.conclusioni}`;
      const blob = new Blob([content], {
        type: 'application/pdf'
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Referto_${exam.id}_${exam.patient.replace(/\s/g, '_')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 500);
  };
  const PatientInfoContent = () => <div className="space-y-4" data-id="element-2054">
      <div data-id="element-2055">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2056">
          Nome e Cognome
        </p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2057">
          {exam.patient}
        </p>
      </div>
      <div data-id="element-2058">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2059">
          Codice Fiscale
        </p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2060">
          {exam.codiceFiscale}
        </p>
      </div>
      <div data-id="element-2061">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2062">Email</p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2063">
          {exam.email}
        </p>
      </div>
      <div data-id="element-2064">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2065">Telefono</p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2066">
          {exam.telefono}
        </p>
      </div>
    </div>;
  const ExamInfoContent = () => <div className="space-y-4" data-id="element-2067">
      <div data-id="element-2068">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2069">
          Prestazione / Tipo esame
        </p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2070">
          {exam.type}
        </p>
      </div>
      <div data-id="element-2071">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2072">
          Data e ora esame
        </p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2073">
          {exam.date} alle {exam.time}
        </p>
      </div>
      <div data-id="element-2074">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2075">
          Macchinario utilizzato
        </p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2076">
          {exam.machine}
        </p>
      </div>
      <div data-id="element-2077">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2078">
          Medico Refertatore
        </p>
        <p className="text-sm md:text-body font-medium text-text-primary" data-id="element-2079">
          {exam.doctor}
        </p>
      </div>
      <div data-id="element-2080">
        <p className="text-xs md:text-small text-text-tertiary" data-id="element-2081">Status Esame</p>
        <div className="mt-1" data-id="element-2082">
          <StatusBadge status={exam.status} data-id="element-2083" />
        </div>
      </div>
    </div>;
  const TemplatesContent = () => <div className="space-y-3" data-id="element-2084">
      {availableTemplates.length === 0 ? <p className="text-sm text-text-secondary" data-id="element-2085">
          Nessun modello disponibile per questa categoria.
        </p> : availableTemplates.map(template => <div key={template.id} onClick={() => setSelectedTemplate(template.id)} className={`p-3 border rounded-medical transition-colors group cursor-pointer ${selectedTemplate === template.id ? 'border-medical-green bg-medical-green-50 ring-1 ring-medical-green' : 'border-clinical-gray-200 hover:border-medical-green/50 hover:bg-clinical-gray-50'}`} data-id="element-2086">
            <div className="flex items-start gap-3" data-id="element-2087">
              <FileTextIcon size={18} className={`mt-0.5 shrink-0 ${selectedTemplate === template.id ? 'text-medical-green' : 'text-text-tertiary group-hover:text-medical-green'}`} data-id="element-2088" />
              <div data-id="element-2089">
                <p className={`text-sm font-medium ${selectedTemplate === template.id ? 'text-medical-green' : 'text-text-primary group-hover:text-medical-green'}`} data-id="element-2090">
                  {template.name}
                </p>
                <p className="text-xs text-text-tertiary mt-1" data-id="element-2091">
                  {template.description}
                </p>
              </div>
            </div>
          </div>)}
    </div>;
  const renderActions = () => {
    if (exam.status === 'Da refertare' || exam.status === 'In corso') {
      return <>
          <button className="medical-btn-secondary w-full justify-center" onClick={() => handleConfirmReport(false)} data-id="element-2092">
            <CheckCircleIcon size={18} className="mr-2" data-id="element-2093" /> Conferma e salva
          </button>
          <button className="w-full justify-center inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-medical border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 transition-colors" onClick={() => handleConfirmReport(true)} data-id="element-2094">
            <SendIcon size={18} className="mr-2" data-id="element-2095" /> Conferma e invia
          </button>
          <p className="text-xs text-text-tertiary text-center mt-2" data-id="element-2096">
            L'invio notificherà immediatamente il paziente.
          </p>
        </>;
    }
    if (exam.status === 'Refertato') {
      return <>
          <button className="medical-btn-secondary w-full justify-center" onClick={handleDownloadPDF} data-id="element-2097">
            <FileDownIcon size={18} className="mr-2" data-id="element-2098" /> Scarica PDF
          </button>
          <button className="w-full justify-center inline-flex items-center px-4 py-2.5 text-sm font-medium rounded-medical border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 transition-colors" onClick={handleSendToPatient} data-id="element-2099">
            <SendIcon size={18} className="mr-2" data-id="element-2100" /> Invia al paziente
          </button>
        </>;
    }
    if (exam.status === 'Consegnato') {
      return <>
          <button className="medical-btn-secondary w-full justify-center" onClick={handleDownloadPDF} data-id="element-2101">
            <FileDownIcon size={18} className="mr-2" data-id="element-2102" /> Scarica PDF
          </button>
          <button onClick={handleSendToPatient} className="medical-btn-secondary w-full justify-center text-medical-green border-medical-green/30 hover:bg-medical-green-50" data-id="element-2103">
            <RefreshCwIcon size={18} className="mr-2" data-id="element-2104" /> Reinvia al paziente
          </button>
        </>;
    }
    return null;
  };
  const SaveStatusIndicator = () => {
    if (saveStatus === 'idle' && !lastSaved) return null;
    return <div className="flex items-center gap-1.5 text-xs" data-id="element-2105">
        {saveStatus === 'unsaved' && <span className="text-medical-orange flex items-center gap-1" data-id="element-2106">
            <span className="w-1.5 h-1.5 rounded-full bg-medical-orange animate-pulse" data-id="element-2107"></span>
            Modifiche non salvate
          </span>}
        {saveStatus === 'saving' && <span className="text-text-secondary flex items-center gap-1" data-id="element-2108">
            <RefreshCwIcon size={12} className="animate-spin" data-id="element-2109" />
            Salvataggio in corso...
          </span>}
        {saveStatus === 'saved' && lastSaved && <span className="text-text-tertiary flex items-center gap-1" data-id="element-2110">
            <CheckCircleIcon size={12} className="text-medical-green" data-id="element-2111" />
            Ultimo salvataggio: {formatTimestamp(lastSaved)}
          </span>}
      </div>;
  };
  return <div className="flex flex-col h-full min-h-[calc(100vh-8rem)] -m-4 md:-m-8" data-id="element-2112">
      {/* Header */}
      <div className="bg-white border-b border-clinical-gray-100 px-4 md:px-8 py-3 md:py-4 flex items-center justify-between shrink-0 sticky top-0 z-10" data-id="element-2113">
        <div className="flex items-start gap-2 md:gap-4" data-id="element-2114">
          <button onClick={() => navigate('/exams')} className="p-2 hover:bg-clinical-gray rounded-full transition-colors text-text-secondary mt-0.5" data-id="element-2115">
            <ArrowLeftIcon size={20} data-id="element-2116" />
          </button>
          <div data-id="element-2117">
            <div className="flex items-center gap-2 md:gap-3 flex-wrap" data-id="element-2118">
              <h1 className="text-lg md:text-section-title font-semibold text-text-primary" data-id="element-2119">
                Editor Referto Medico
              </h1>
              <StatusBadge status={exam.status} data-id="element-2120" />
            </div>
            <div className="flex items-center gap-3 mt-0.5" data-id="element-2121">
              <p className="text-xs md:text-small text-text-secondary" data-id="element-2122">
                ID Esame: {exam.id}
              </p>
              <SaveStatusIndicator data-id="element-2123" />
            </div>
          </div>
        </div>
      </div>

      {/* Workspace - Full Height */}
      <div className={`flex-1 flex ${isMobile ? 'flex-col' : 'overflow-hidden'} bg-clinical-gray`} data-id="element-2124">
        {/* Mobile View */}
        {isMobile && <div className="p-4 space-y-4 pb-44 flex-1" data-id="element-2125">
            {/* Accordions for Info */}
            <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 overflow-hidden" data-id="element-2126">
              <button className="w-full px-4 py-3 flex items-center justify-between bg-clinical-gray-50 border-b border-clinical-gray-100" onClick={() => setPatientInfoOpen(!patientInfoOpen)} data-id="element-2127">
                <span className="text-sm font-semibold text-text-primary" data-id="element-2128">
                  Dati paziente
                </span>
                {patientInfoOpen ? <ChevronUpIcon size={18} data-id="element-2129" /> : <ChevronDownIcon size={18} data-id="element-2130" />}
              </button>
              {patientInfoOpen && <div className="p-4" data-id="element-2131">
                  <PatientInfoContent data-id="element-2132" />
                </div>}
            </div>

            <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 overflow-hidden" data-id="element-2133">
              <button className="w-full px-4 py-3 flex items-center justify-between bg-clinical-gray-50 border-b border-clinical-gray-100" onClick={() => setExamInfoOpen(!examInfoOpen)} data-id="element-2134">
                <span className="text-sm font-semibold text-text-primary" data-id="element-2135">
                  Dettagli prestazione
                </span>
                {examInfoOpen ? <ChevronUpIcon size={18} data-id="element-2136" /> : <ChevronDownIcon size={18} data-id="element-2137" />}
              </button>
              {examInfoOpen && <div className="p-4" data-id="element-2138">
                  <ExamInfoContent data-id="element-2139" />
                </div>}
            </div>

            {/* Editor */}
            <div className="space-y-4 md:space-y-6" data-id="element-2140">
              <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2141">
                <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2142">
                  Introduzione / Indicazione
                </label>
                <textarea className="medical-input min-h-[80px] md:min-h-[100px] resize-y w-full" value={reportForm.intro} onChange={e => setReportForm(prev => ({
              ...prev,
              intro: e.target.value
            }))} placeholder="Inserire indicazioni cliniche..." data-id="element-2143" />
              </div>
              <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2144">
                <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2145">
                  Reperti
                </label>
                <textarea className="medical-input min-h-[150px] md:min-h-[200px] resize-y w-full" value={reportForm.rilevazioni} onChange={e => setReportForm(prev => ({
              ...prev,
              rilevazioni: e.target.value
            }))} placeholder="Inserire i reperti oggettivi..." data-id="element-2146" />
              </div>
              <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2147">
                <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2148">
                  Valutazione clinica
                </label>
                <textarea className="medical-input min-h-[100px] md:min-h-[150px] resize-y w-full" value={reportForm.clinica} onChange={e => setReportForm(prev => ({
              ...prev,
              clinica: e.target.value
            }))} placeholder="Inserire la valutazione clinica..." data-id="element-2149" />
              </div>
              <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2150">
                <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2151">
                  Conclusione
                </label>
                <textarea className="medical-input min-h-[80px] md:min-h-[100px] resize-y w-full" value={reportForm.conclusioni} onChange={e => setReportForm(prev => ({
              ...prev,
              conclusioni: e.target.value
            }))} placeholder="Inserire le conclusioni diagnostiche..." data-id="element-2152" />
              </div>
            </div>

            {/* Templates Accordion */}
            <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 overflow-hidden" data-id="element-2153">
              <button className="w-full px-4 py-3 flex items-center justify-between bg-clinical-gray-50 border-b border-clinical-gray-100" onClick={() => setTemplatesOpen(!templatesOpen)} data-id="element-2154">
                <span className="text-sm font-semibold text-text-primary" data-id="element-2155">
                  Modelli Referto
                </span>
                {templatesOpen ? <ChevronUpIcon size={18} data-id="element-2156" /> : <ChevronDownIcon size={18} data-id="element-2157" />}
              </button>
              {templatesOpen && <div className="p-4" data-id="element-2158">
                  <TemplatesContent data-id="element-2159" />
                </div>}
            </div>
          </div>}

        {/* Desktop/Tablet View */}
        {!isMobile && <>
            {/* LEFT PANEL: Patient & Exam Info */}
            {!isTablet && <div className="w-[280px] bg-white border-r border-clinical-gray-100 overflow-y-auto p-6 shrink-0 h-full" data-id="element-2160">
                <h3 className="text-table-header text-text-secondary uppercase tracking-wider mb-4" data-id="element-2161">
                  Dati paziente
                </h3>
                <div className="mb-8" data-id="element-2162">
                  <PatientInfoContent data-id="element-2163" />
                </div>
                <h3 className="text-table-header text-text-secondary uppercase tracking-wider mb-4" data-id="element-2164">
                  Dettagli prestazione
                </h3>
                <ExamInfoContent data-id="element-2165" />
              </div>}

            {/* CENTER PANEL: Editor */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8 h-full" data-id="element-2166">
              <div className="max-w-3xl mx-auto" data-id="element-2167">
                <div className="space-y-4 md:space-y-6" data-id="element-2168">
                  <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2169">
                    <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2170">
                      Introduzione / Indicazione
                    </label>
                    <textarea className="medical-input min-h-[80px] md:min-h-[100px] resize-y w-full" value={reportForm.intro} onChange={e => setReportForm(prev => ({
                  ...prev,
                  intro: e.target.value
                }))} placeholder="Inserire indicazioni cliniche..." data-id="element-2171" />
                  </div>
                  <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2172">
                    <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2173">
                      Reperti
                    </label>
                    <textarea className="medical-input min-h-[150px] md:min-h-[200px] resize-y w-full" value={reportForm.rilevazioni} onChange={e => setReportForm(prev => ({
                  ...prev,
                  rilevazioni: e.target.value
                }))} placeholder="Inserire i reperti oggettivi..." data-id="element-2174" />
                  </div>
                  <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2175">
                    <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2176">
                      Valutazione clinica
                    </label>
                    <textarea className="medical-input min-h-[100px] md:min-h-[150px] resize-y w-full" value={reportForm.clinica} onChange={e => setReportForm(prev => ({
                  ...prev,
                  clinica: e.target.value
                }))} placeholder="Inserire la valutazione clinica..." data-id="element-2177" />
                  </div>
                  <div className="bg-white rounded-medical-lg shadow-sm border border-clinical-gray-100 p-4 md:p-6" data-id="element-2178">
                    <label className="block text-sm md:text-table-header text-text-primary mb-2" data-id="element-2179">
                      Conclusione
                    </label>
                    <textarea className="medical-input min-h-[80px] md:min-h-[100px] resize-y w-full" value={reportForm.conclusioni} onChange={e => setReportForm(prev => ({
                  ...prev,
                  conclusioni: e.target.value
                }))} placeholder="Inserire le conclusioni diagnostiche..." data-id="element-2180" />
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT PANEL: Templates & Actions */}
            <div className="w-[280px] lg:w-[320px] bg-white border-l border-clinical-gray-100 flex flex-col shrink-0 h-full" data-id="element-2181">
              <div className="p-6 flex-1 overflow-y-auto" data-id="element-2182">
                <h3 className="text-table-header text-text-secondary uppercase tracking-wider mb-4" data-id="element-2183">
                  Modelli
                </h3>
                <TemplatesContent data-id="element-2184" />
              </div>
              <div className="p-6 border-t border-clinical-gray-100 bg-clinical-gray-50 space-y-3 shrink-0" data-id="element-2185">
                {renderActions()}
              </div>
            </div>
          </>}
      </div>

      {/* Mobile Fixed Actions Bottom Bar */}
      {isMobile && <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-clinical-gray-200 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] z-20 space-y-2" data-id="element-2186">
          {renderActions()}
        </div>}

      {/* Toast Notification */}
      <AnimatePresence data-id="element-2187">
        {toastMessage && <motion.div initial={{
        opacity: 0,
        y: 50,
        scale: 0.9
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 20,
        scale: 0.9
      }} className={`fixed bottom-6 left-1/2 -translate-x-1/2 sm:left-auto sm:translate-x-0 sm:right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-medical-lg shadow-modal border bg-white w-[calc(100%-3rem)] sm:w-auto ${toastMessage.type === 'success' ? 'border-status-success/20' : 'border-status-error/20'}`} data-id="element-2188">
            {toastMessage.type === 'success' ? <CheckCircleIcon className="text-status-success shrink-0" size={20} data-id="element-2189" /> : <AlertCircleIcon className="text-status-error shrink-0" size={20} data-id="element-2190" />}
            <span className="text-sm font-medium text-text-primary" data-id="element-2191">
              {toastMessage.text}
            </span>
            <button onClick={() => setToastMessage(null)} className="ml-2 p-1 text-text-tertiary hover:text-text-primary rounded-full transition-colors" data-id="element-2192">
              <XIcon size={16} data-id="element-2193" />
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}